import {
  EvidencingFragment,
  FullResourceFragment,
} from "~/graphql/codegen/graphql";
import boltGradientSvg from "~/assets/bolt-gradient.svg";
import { Link, Location, useLocation, useSearchParams } from "react-router";
import {
  ArrowLeftIcon,
  BookmarkIcon,
  ChatBubbleBottomCenterIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  SparklesIcon,
  Squares2X2Icon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { getScoreGrade } from "~/utils/get-score-grade";
import { Button, Switch } from "@mindstonehq/ui";
import React from "react";
import {
  isFocused,
  useUpdateFocus,
} from "~/components/annotate/hooks/focus-mode";
import { AnnotatePanels } from "./annotate";
import { LogoSmall, LogoSmallDark, NoteIcon, ScoreIcon } from "../icons";
import { classNames } from "~/utils";
import { ResourceType } from "~/utils/resource-icon";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

// eslint-disable-next-line lingui/no-unlocalized-strings -- Tailwind class names
const sidebarRowClasses = "flex items-center space-x-2";
// eslint-disable-next-line lingui/no-unlocalized-strings -- Tailwind class names
const sidebarTitleClass = "text-placeholder-foreground text-sm";
export default function SidebarContent({
  article,
  evidencing,
  setPanel,
}: {
  article: FullResourceFragment;
  evidencing: EvidencingFragment;
  setPanel: (panel: AnnotatePanels) => void;
}) {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const gobackUrl = React.useMemo(() => {
    if (location.state?.back && location.state.back.pathname) {
      const backLocation = location.state.back as Location;
      return `${backLocation.pathname}${backLocation.search ? `${backLocation.search}` : ""}`;
    } else {
      return "/";
    }
  }, [location.state]);
  const focused = isFocused();
  const setFocus = useUpdateFocus();
  const highlightWithPrivateNotes = article.annotations.highlights.filter(
    (highlight) => !!highlight.privateNote,
  );
  const highlightsWithPublicComments = article.annotations.highlights.filter(
    (highlight) => highlight.comments.length,
  );

  return (
    <div className="h-full w-full  flex flex-col justify-between">
      <div className="flex w-full flex-col space-y-12">
        <Link className="hidden md:block " to={"/"}>
          <LogoSmall className={"dark:hidden"} />
          <LogoSmallDark className={"hidden dark:block"} />
        </Link>
        <div className="hidden md:flex flex-col space-y-3">
          <Button
            variant="link"
            asChild
            className={sidebarRowClasses + " justify-start px-0 py-0"}
          >
            <Link to={`${gobackUrl}`}>
              <ArrowLeftIcon className="h-6 w-6 shrink-0" />
              <span><Trans>Go Back</Trans></span>
            </Link>
          </Button>
        </div>
        <div className="flex flex-col space-y-3">
          {article.url && (
            <a
              className={classNames(
                sidebarRowClasses,
                " text-foreground hover:text-muted-foreground",
              )}
              href={article.url || "#"}
              target={article.url ? "_blank" : ""}
            >
              <GlobeAltIcon className="h-6 w-6 shrink-0" />
              <span className="line-clamp-1">
                {article.siteName || article.url}
              </span>
            </a>
          )}
          {article.location?.__typename === "Mindspace" && (
            <div className={sidebarRowClasses}>
              <Squares2X2Icon className="h-6 w-6 shrink-0" />
              <span className="line-clamp-1">{article.location.name}</span>
            </div>
          )}
          {article.location?.__typename === "InLibrary" && (
            <div className={sidebarRowClasses}>
              <BookmarkIcon className="h-6 w-6 shrink-0" />
              <span className="line-clamp-1"><Trans>In Library</Trans></span>
            </div>
          )}
          {article.suggested && (
            <div className={sidebarRowClasses}>
              <SparklesIcon className="h-6 w-6 shrink-0" />
              <span className="line-clamp-1"><Trans>Suggested by AI</Trans></span>
            </div>
          )}
          {article.readingTime && (
            <div className={sidebarRowClasses}>
              {article.type === ResourceType.VIDEO ? (
                <VideoCameraIcon className="h-6 w-6 shrink-0" />
              ) : (
                <DocumentTextIcon className="h-6 w-6 shrink-0" />
              )}
              <span>{article.readingTime}</span>
            </div>
          )}
          {article.evidencing && (
            <div className={sidebarRowClasses}>
              <img
                src={boltGradientSvg}
                alt={t(msg`points`)}
                className="h-6 w-6 shrink-0"
              />
              <span>{Math.round(evidencing.totalTriviaEarnablePoints)}</span>
            </div>
          )}
        </div>
        {(highlightWithPrivateNotes.length > 0 ||
          highlightsWithPublicComments.length > 0) && (
          <div className="flex flex-col">
            <div className={sidebarTitleClass}>
              <span className="line-clamp-1"><Trans>Activity</Trans></span>
            </div>{" "}
            {highlightWithPrivateNotes.length > 0 && (
              <Button
                onClick={() => setPanel(AnnotatePanels.ResourceNotes)}
                variant="link"
                className={
                  sidebarRowClasses +
                  "text-primary-foreground justify-start pl-0"
                }
              >
                <NoteIcon className="h-4 w-4" />
                <span><Trans>My private notes</Trans></span>
              </Button>
            )}
            {highlightsWithPublicComments.length > 0 && (
              <Button
                onClick={() => setPanel(AnnotatePanels.ResourceComments)}
                variant="link"
                className={sidebarRowClasses + " justify-start pl-0"}
              >
                <ChatBubbleBottomCenterIcon className="h-4 w-4" />
                <span><Trans>Public comments</Trans></span>
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-col space-y-3">
          <div className={sidebarTitleClass}>
            <span className="line-clamp-1"><Trans>Tags</Trans></span>
          </div>
          <div className={sidebarRowClasses}>{article.tags.join(", ")}</div>
        </div>
      </div>
      <div className="flex flex-col space-y-3">
        <div className={sidebarRowClasses}>
          <Switch onCheckedChange={setFocus} checked={focused} />
          <span className="line-clamp-1"><Trans>Focus mode</Trans></span>
        </div>
      </div>
    </div>
  );
}
